/**
 * Cross-domain catalogs.
 *
 * These live outside any feature because more than one module reads them:
 * shipping zones assign coverage by method, and order upload lets the operator
 * pick the method an order travels with.
 */

/**
 * Shipping methods supported by the platform.
 *
 * Every `value` is persisted by the backend, so they must not be renamed or
 * deduplicated — including the two "not selected" variants, which coexist in
 * older records.
 *
 * @type {Array<{value: string, label: string}>}
 */
export const SHIPPING_METHOD_OPTIONS = [
  { value: 'NOT_SELECTED', label: 'No Seleccionado' },
  { value: 'express', label: 'Envío Express' },
  { value: 'same_day', label: 'Envío Same day' },
  { value: 'regional', label: 'Envío Regional' },
  { value: 'rm_rural', label: 'Envío RM Rural' },
  { value: 'pod', label: 'Envío a Punto de Despacho' },
  { value: 'rm_same_day', label: 'Envío RM Same day' },
  { value: 'rm_next_day', label: 'Envío RM Next day' },
  { value: 'meli', label: 'Envío Meli' },
  { value: 'meli_flex', label: 'Envío Meli Flex' },
  { value: 'meli_places', label: 'Envío Meli Places' },
  { value: 'region', label: 'Envío Región' },
  { value: 'pick_up_goex', label: 'Pick Up GO-EX' },
  { value: 'pick_up_bluex', label: 'Pick Up Bluex' },
  { value: 'devolution_bluex', label: 'Devolución Bluex' },
  { value: 'not_selected', label: 'Not Selected' },
  { value: 'express_uber', label: 'Envío Express Uber' },
];

/**
 * Resolves the human-readable label of a shipping method system name.
 *
 * @param {string} systemName
 * @returns {string}
 */
export const getShippingMethodLabel = (systemName) => {
  const option = SHIPPING_METHOD_OPTIONS.find((item) => item.value === systemName);
  return option?.label || systemName || '-';
};

/**
 * Delivery statuses and their substatuses, with the colour each one is painted
 * with in tables and badges.
 *
 * Moved here from the admin workspace: the order-detail feature needs the same
 * catalog to build its status timeline and its status editor, and a feature
 * must not import from a workspace.
 */
export const DELIVERY_STATUSES = [
  {
    id: 1,
    system_name: "pending",
    user_name: "Pendiente",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 2,
    system_name: "created_on_courier",
    user_name: "Creado en courier",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 16,
    system_name: "fulfilled",
    user_name: "Preparado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 3,
    system_name: "ready_for_pickup",
    user_name: "Listo para retiro",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 4,
    system_name: "withdrawn",
    user_name: "Retirado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
      { id: 4, system_name: "internal_reader", user_name: "Gestión interna" },
      { id: 5, system_name: "external_reader", user_name: "Gestión externa" },
    ],
    color: "#D3D3D3",
  },
  {
    // The warehouse handed the boxes to the courier at its door (WMS,
    // "Despachar"). Not the same as "Retirado", which the courier reports
    // itself; for the stock ledger both mean the goods left.
    id: 20,
    system_name: "delivered_to_courier",
    user_name: "Entregado a courier",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 5,
    system_name: "in_transit",
    user_name: "En tránsito",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#ffdf80",
  },
  {
    id: 6,
    system_name: "in_cd",
    user_name: "En CD",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#ffdf80",
  },
  {
    id: 7,
    system_name: "dropped_at_hub",
    user_name: "Dejado en hub",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#ffdf80",
  },
  {
    // Puntos de retiro: el punto escaneó los bultos que el courier le dejó.
    id: 21,
    system_name: "received_at_hub",
    user_name: "Recibido en hub",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#ffdf80",
  },
  {
    id: 8,
    system_name: "on_the_way",
    user_name: "En camino",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#ffdf80",
  },
  {
    id: 9,
    system_name: "delivered",
    user_name: "Entregado",
    substatus: [
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#36D9C0",
  },
  {
    id: 10,
    system_name: "failed",
    user_name: "Fallido",
    substatus: [
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
      { id: 4, system_name: "rejected", user_name: "Rechazado" },
      { id: 5, system_name: "incomplete_address", user_name: "Dirección Incompleta" },
      { id: 6, system_name: "absent_client", user_name: "Cliente ausente" },
      { id: 7, system_name: "nonexisting_address", user_name: "Dirección no existe" },
      { id: 8, system_name: "out_of_range", user_name: "Sin cobertura" },
      { id: 9, system_name: "bad_data", user_name: "Datos erróneos" },
      { id: 10, system_name: "route_pending", user_name: "Pendiente de ruta" },
      { id: 12, system_name: "product_not_match", user_name: "Producto no corresponde" },
      { id: 12, system_name: "rescheduled", user_name: "Reprogramado" },
      { id: 13, system_name: "out_of_schedule", user_name: "Fuera de horario" },
      { id: 14, system_name: "not_delivered", user_name: "No entregado" },
    ],
    color: "#ff6666",
  },
  {
    id: 11,
    system_name: "returning",
    user_name: "En devolución",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#6446d2",
  },
  {
    id: 12,
    system_name: "returned",
    user_name: "Devuelto",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#6446d2",
  },
  {
    id: 13,
    system_name: "damaged",
    user_name: "Siniestrado",
    substatus: [
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
      { id: 12, system_name: "spoiled_package", user_name: "Embalaje dañado" },
      { id: 13, system_name: "missing_package", user_name: "Extraviado" },
      { id: 14, system_name: "spoiled_content", user_name: "Contenido dañado" },
    ],
    color: "#B3122D",
  },
  {
    id: 14,
    system_name: "compensated",
    user_name: "Indemnizado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#33800D",
  },
  {
    id: 15,
    system_name: "cancelled",
    user_name: "Cancelado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#B3122D",
  },
  {
    // 17, not 16: `fulfilled` above already carries 16. Nothing reads these ids
    // — every lookup goes through `system_name` — but a duplicate is a trap for
    // whoever does read them first.
    id: 17,
    system_name: "assigned",
    user_name: "Asignado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
      { id: 2, system_name: "partial", user_name: "Parcial" },
      { id: 3, system_name: "complete", user_name: "Completo" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 18,
    system_name: "waiting_for_assignment",
    user_name: "Por asignar",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
    ],
    color: "#D3D3D3",
  },
  {
    id: 19,
    system_name: "packed",
    user_name: "Pedido armado",
    substatus: [
      { id: 1, system_name: "not_specified", user_name: "No aplica" },
    ],
    color: "#D3D3D3",
  },
];

export const STATUS_MAP = DELIVERY_STATUSES.reduce((acc, status) => {
  acc[status.system_name] = status;
  return acc;
}, {});

/**
 * Substatus labels, flattened across every status.
 *
 * The timeline needs to label a substatus without knowing which status it was
 * recorded under, because historical rows can carry a substatus that no longer
 * hangs from their status in the catalog. `not_specified` maps to an empty
 * string on purpose: the timeline renders "Entregado", never "Entregado: No
 * aplica".
 *
 * @type {Object.<string, string>}
 */
export const SUBSTATUS_LABELS = DELIVERY_STATUSES.reduce(
  (acc, status) => {
    status.substatus.forEach((substatus) => {
      if (substatus.system_name === 'not_specified') return;
      acc[substatus.system_name] = substatus.user_name;
    });
    return acc;
  },
  { not_specified: '' }
);
